import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { fetchTemplates, fetchTemplateById, deleteTemplate } from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";
import "./TemplatesPage.css";

const DAYS_OF_WEEK = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// Group template stops by employee+day, sorted within each group by routeOrder.
const groupStops = (stops) => {
  const map = new Map();
  (stops || []).forEach((stop) => {
    const key = `${stop.employeeId ?? ""}|${stop.dayOfWeek ?? ""}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        employeeName: stop.employeeName || null,
        dayOfWeek: stop.dayOfWeek,
        stops: [],
      });
    }
    map.get(key).stops.push(stop);
  });
  map.forEach((g) => g.stops.sort((a, b) => (a.routeOrder ?? 999) - (b.routeOrder ?? 999)));
  return Array.from(map.values()).sort((a, b) => {
    const d = (a.dayOfWeek ?? 7) - (b.dayOfWeek ?? 7);
    return d !== 0 ? d : (a.employeeName || "").localeCompare(b.employeeName || "");
  });
};

const TemplatesPage = () => {
  const navigate = useNavigate();
  const { companyId, userDetails } = useAuth();

  const canManage = ["Owner", "Manager", "Co-Owner", "Admin"].includes(
    userDetails?.role || userDetails?.designation
  );

  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (companyId) loadTemplates();
  }, [companyId, userDetails?.employeeId]);

  const loadTemplates = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchTemplates(companyId, userDetails?.employeeId ?? null);
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      setError("Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (template) => {
    setLoadingDetail(true);
    setError("");
    try {
      const detail = await fetchTemplateById(template.templateId, companyId);
      setSelected(detail);
    } catch {
      setError("Failed to load template details.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template? This cannot be undone.")) return;
    setDeleting(true);
    setError("");
    try {
      await deleteTemplate(templateId, companyId);
      setTemplates((prev) => prev.filter((t) => t.templateId !== templateId));
      if (selected?.templateId === templateId) setSelected(null);
    } catch {
      setError("Failed to delete template.");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = templates.filter((t) =>
    t.templateName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box className="templates-page-root">
      <Box className="templates-page-header">
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#002244" }}>
            Schedule Templates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Saved route templates for your company. Import them from the scheduling page.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/employee-scheduling")}
          sx={{ backgroundColor: "#002244", whiteSpace: "nowrap" }}
        >
          Create Template
        </Button>
      </Box>

      <Paper className="templates-page-body" elevation={2}>
        {/* Left panel — list */}
        <Box className="templates-list-panel">
          <TextField
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ marginBottom: "12px" }}
            inputProps={{ autoCorrect: "off", spellCheck: false }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "32px" }}>
              <CircularProgress size={32} />
            </Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ padding: "16px 0" }}>
              {templates.length === 0
                ? "No templates saved yet. Go to Employee Scheduling and save one."
                : "No templates match your search."}
            </Typography>
          ) : (
            <List dense disablePadding>
              {filtered.map((t) => (
                <React.Fragment key={t.templateId}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      canManage && (
                        <Tooltip title="Delete template">
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleting}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(t.templateId);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    <ListItemButton
                      selected={selected?.templateId === t.templateId}
                      onClick={() => handleSelect(t)}
                      sx={{ paddingRight: canManage ? "44px" : "12px", borderRadius: "6px" }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span>{t.templateName}</span>
                            {!t.isShared && (
                              <Chip label="Private" size="small" sx={{ fontSize: "10px", height: "18px" }} />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            {t.stops?.length ?? 0} stop{(t.stops?.length ?? 0) !== 1 ? "s" : ""}
                            {t.dayOfWeek != null ? ` · ${DAYS_OF_WEEK[t.dayOfWeek]}` : ""}
                          </>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Vertical divider */}
        <Divider orientation="vertical" flexItem />

        {/* Right panel — detail */}
        <Box className="templates-detail-panel">
          {loadingDetail ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "32px" }}>
              <CircularProgress size={32} />
            </Box>
          ) : selected ? (
            <>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#002244", marginBottom: "4px" }}>
                {selected.templateName}
              </Typography>

              <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "8px" }}>
                {selected.dayOfWeek != null && (
                  <Chip
                    label={DAYS_OF_WEEK[selected.dayOfWeek]}
                    size="small"
                    sx={{ backgroundColor: "#e8edf4", color: "#002244" }}
                  />
                )}
                <Chip
                  label={selected.isShared ? "Shared" : "Private"}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`${selected.stops?.length ?? 0} stops`}
                  size="small"
                  variant="outlined"
                />
              </Box>

              {selected.origEmpName && (
                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "4px" }}>
                  Originally built for: <strong>{selected.origEmpName}</strong>
                </Typography>
              )}

              {selected.description && (
                <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "12px" }}>
                  {selected.description}
                </Typography>
              )}

              <Divider sx={{ marginBottom: "12px" }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: "8px", color: "#002244" }}>
                Route Order
              </Typography>

              <Box className="templates-stop-list">
                {groupStops(selected.stops).map((group) => (
                  <Box key={group.key}>
                    {(group.employeeName || group.dayOfWeek != null) && (
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: "#002244", display: "block",
                              mt: "10px", mb: "4px", textTransform: "uppercase", letterSpacing: ".5px" }}
                      >
                        {[group.employeeName, group.dayOfWeek != null ? DAYS_OF_WEEK[group.dayOfWeek] : null]
                          .filter(Boolean).join(" · ")}
                      </Typography>
                    )}
                    {group.stops.map((stop, idx) => (
                      <Box key={stop.stopId} className="templates-stop-row">
                        <Typography variant="body2" className="stop-order-badge">
                          {idx + 1}
                        </Typography>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {stop.customerFirstName} {stop.customerLastName}
                          </Typography>
                          {stop.customerAddress && (
                            <Typography variant="caption" color="text.secondary">
                              {stop.customerAddress}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>

              <Box sx={{ marginTop: "20px" }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/employee-scheduling")}
                  sx={{ borderColor: "#002244", color: "#002244" }}
                >
                  Use on Scheduling Page
                </Button>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <Typography variant="body2" color="text.secondary">
                Select a template to see its stop order.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {error && (
        <Typography variant="body2" color="error" sx={{ marginTop: "12px" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default TemplatesPage;
